import type { TFileInputObject } from '$/types/components/questions';
import type { InputProps } from '../TextInput';
import type { FileRejection } from 'react-dropzone';

import {
  faExclamationCircle,
  faPlusCircle,
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Auth, Storage } from 'aws-amplify';
import React, { useEffect, useState } from 'react';
import { useDropzone } from 'react-dropzone';

import awsmobile from '../../../../aws-exports';
import ErrorMessage from '../../ErrorMessage';
import { Sub2Typography } from '../../Typography';
import StatusIconContainer from '../TextInput/StatusIconContainer';

import { acceptedFileTypes, fileSizeLimit } from './const';
import SelectedFile from './SelectedFile/SelectedFile';
import StyledLabel from './StyledLabel';

import theme from '$/assets/theme';
import handleCommonToast from '$/utils/handleCommonToast';

type AdditionalDocumentPropsType = {
  changeDataId?: number;
  semesterInfoId?: number | null;
  voluntaryUpdateId?: number;
  fileNameOverride?: string;
  type?: string;
};

export interface IFileInputProps {
  name: string;
  value: TFileInputObject | null;
  additionalDocumentProps?: AdditionalDocumentPropsType;
  onSuccess?: (document: TFileInputObject) => Promise<void>;
  onCancel?: () => void;
  error?: InputProps['error'];
  errorMessage?: InputProps['errorMessage'];
  errorMessageColor?: InputProps['errorMessageColor'];
}

const FileInput = (props: IFileInputProps) => {
  const [immutableObject] = useState<{
    storagePutPromise: null | Promise<unknown>;
  }>({ storagePutPromise: null });

  const [uploadedFileName, setUploadedFileName] = useState<string | null>(null);
  const [progress, setProgress] = useState(props.value ? 100 : 0);
  const [processing, setProcessing] = useState<boolean>(false);

  const resetInput = async () => {
    props.onCancel?.();
    setProgress(0);
    setUploadedFileName(null);
    if (immutableObject.storagePutPromise) {
      Storage.cancel(
        immutableObject.storagePutPromise,
        'user cancelled upload'
      );
    }
  };

  const handleUploadFile = async (file: File) => {
    try {
      setProcessing(true);
      setUploadedFileName(file.name);

      const timestamp = Date.now();
      const fileExtension = file.name.split('.').pop();
      const key = `${props.name}-${timestamp}.${fileExtension}`;
      const level = 'private';

      const { identityId } = await Auth.currentCredentials();

      if (!identityId) {
        handleCommonToast(
          'error',
          'Benutzerauthentifizierung fehlt, bitte versuchen Sie es erneut',
          {},
          new Error('Missing identity id')
        );
        return;
      }

      immutableObject.storagePutPromise = Storage.put(key, file, {
        region: awsmobile.aws_user_files_s3_bucket_region,
        level,
        progressCallback(progress: { loaded: number; total: number }) {
          const newProgress = Math.round(
            (progress.loaded / progress.total) * 100
          );
          setProgress(newProgress);
        },
      });

      await immutableObject.storagePutPromise;
      immutableObject.storagePutPromise = null;

      // this is the actual key under which the file is being stored in S3
      const s3Key = `${level}/${identityId}/${key}`;

      setProgress(100);

      const additionalProps = props.additionalDocumentProps || {};

      const document = { ...additionalProps, key: s3Key, name: file.name };
      await props.onSuccess?.(document);
    } catch (error) {
      // We can confirm the error is thrown by the cancellation here
      if (Storage.isCancelError(error)) {
        // do nothing
        console.log(error.message);
        return;
      }

      console.error(error);

      // TODO handle if other error
    } finally {
      setUploadedFileName(null);
      setProcessing(false);
    }
  };

  const onDrop = async (acceptedFiles: File[]) => {
    if (!acceptedFiles.length) return;

    const file = acceptedFiles[0];
    if (file) {
      await handleUploadFile(file);
    }
  };

  const onDropRejected = (fileRejections: FileRejection[]) => {
    handleCommonToast(
      'error',
      'WrongUploadedFile',
      {},
      new Error(JSON.stringify(fileRejections))
    );
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    multiple: false,
    accept: acceptedFileTypes,
    onDropRejected,
    maxSize: fileSizeLimit,
  });

  useEffect(() => {
    if (props.value) {
      if (props.value.name && props.value.key) {
        setProgress(100);
        setUploadedFileName(props.value.name);
      }
    } else {
      setProgress(0);
      setUploadedFileName(null);
    }
  }, [props.value]);

  return (
    <div>
      <div style={{ display: progress ? 'none' : 'block' }}>
        <StyledLabel {...getRootProps()} extended={isDragActive}>
          <input name={props.name} {...getInputProps()} />
          <FontAwesomeIcon
            icon={faPlusCircle}
            size="2x"
            color={theme.colors.grey6}
          />
          <Sub2Typography>Datei hinzufügen</Sub2Typography>
        </StyledLabel>
      </div>
      <div style={{ display: progress ? 'block' : 'none' }}>
        <SelectedFile
          name={props.value?.name ?? uploadedFileName ?? ''}
          progress={progress}
          cancelUpload={resetInput}
          processing={!props.value?.name ?? processing}
        />
        {props.error && (
          <>
            <StatusIconContainer>
              <FontAwesomeIcon
                icon={faExclamationCircle}
                color={props.errorMessageColor}
                title={
                  typeof props.errorMessage === 'string'
                    ? props.errorMessage
                    : undefined
                }
              />
            </StatusIconContainer>
            {props.errorMessage && (
              <ErrorMessage color={props.errorMessageColor}>
                {props.errorMessage}
              </ErrorMessage>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default FileInput;
